import { Link } from "react-router-dom";
import { Leaf, Bug, Sparkles, Timer, Calculator, CalendarCheck, BookOpen, Bell, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import { GetTheApp } from "@/components/GetTheApp";

const PATHS = [
  { icon: Bug, name: "Parasite Cleanse", color: "text-lime-400", bg: "bg-lime-400/10 border-lime-500/30",
    desc: "Natural, pharmaceutical or combination protocols with weight-based dosing." },
  { icon: Sparkles, name: "Detox Cleanse", color: "text-teal-300", bg: "bg-teal-400/10 border-teal-500/30",
    desc: "Liver, lymphatic, kidney, colon, full-body & heavy-metal cleanses." },
  { icon: Timer, name: "Water Fasting", color: "text-sky-400", bg: "bg-sky-400/10 border-sky-500/30",
    desc: "Intermittent windows and a gradual 24h → 72h water-fast progression." },
];

const FEATURES = [
  { icon: Calculator, title: "Weight-based dosages", desc: "Every dose scaled to your body weight with clear safety caps." },
  { icon: CalendarCheck, title: "Daily schedule & checklist", desc: "Day-by-day plan with time blocks, hydration & symptom tracking." },
  { icon: BookOpen, title: "Educational library", desc: "Learn parasites, ingredients, die-off and diet prep." },
  { icon: Bell, title: "Reminders", desc: "Morning, midday and evening protocol reminders." },
];

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full bg-teal-500/20 blur-3xl" />
          <div className="absolute top-40 -left-32 h-96 w-96 rounded-full bg-lime-500/10 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 lg:pt-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="pv-in">
            <div className="inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-teal-400/10 px-3 py-1 text-xs font-mono uppercase tracking-widest text-teal-300 mb-6">
              <Sparkles className="h-3.5 w-3.5" /> Personalised cleanse protocols
            </div>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-emerald-50">
              Detox & parasite cleanses,
              <span className="text-teal-400"> built around your body.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-stone-300 leading-relaxed max-w-xl">
              Choose a parasite cleanse, a targeted detox, or a guided water fast. Get weight-adjusted
              dosages and schedules built around you — with education and safety at every step.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/planner">
                <Button data-testid="hero-create-btn" size="lg" className="rounded-full bg-teal-400 hover:bg-teal-300 text-[#0c1311] font-semibold group">
                  Create my protocol
                  <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/learn">
                <Button data-testid="hero-learn-btn" size="lg" variant="outline" className="rounded-full border-emerald-700/50 bg-transparent text-emerald-100 hover:bg-emerald-900/30">
                  Explore free guides
                </Button>
              </Link>
            </div>
          </div>

          <div style={{ animationDelay: "0.15s" }}
            className="pv-pop relative rounded-3xl overflow-hidden border border-emerald-800/40 pv-glow">
            <img
              src="https://images.unsplash.com/photo-1514733670139-4d87a1941d55?auto=format&fit=crop&fm=webp&w=900&h=640&q=70"
              srcSet="https://images.unsplash.com/photo-1514733670139-4d87a1941d55?auto=format&fit=crop&fm=webp&w=600&h=520&q=65 600w, https://images.unsplash.com/photo-1514733670139-4d87a1941d55?auto=format&fit=crop&fm=webp&w=900&h=640&q=70 900w, https://images.unsplash.com/photo-1514733670139-4d87a1941d55?auto=format&fit=crop&fm=webp&w=1200&h=840&q=70 1200w"
              sizes="(min-width: 1024px) 600px, 100vw"
              width="900" height="640" fetchPriority="high" decoding="async"
              alt="Herbal wellness supplements"
              className="w-full h-[420px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0c1311] via-transparent to-transparent" />
          </div>
        </div>
      </section>

      {/* Pathways */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-head text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-emerald-50">Three ways to reset</h2>
        <p className="text-stone-400 mt-2 mb-8">Pick the approach that suits your goals.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {PATHS.map((p, i) => (
            <div key={p.name} style={{ animationDelay: `${i * 0.1}s` }}
              className={`pv-in rounded-2xl border ${p.bg} p-6 hover:-translate-y-1 transition-transform`}>
              <p.icon className={`h-9 w-9 ${p.color}`} />
              <h3 className="font-head text-xl font-semibold mt-4 text-emerald-50">{p.name}</h3>
              <p className="text-sm text-stone-300 mt-2 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <div key={f.title} style={{ animationDelay: `${0.2 + i * 0.08}s` }}
              className="pv-in rounded-2xl border border-emerald-800/40 bg-[#1a2723] p-6">
              <div className="h-11 w-11 rounded-xl bg-teal-400/15 flex items-center justify-center">
                <f.icon className="h-5 w-5 text-teal-300" />
              </div>
              <h4 className="font-head text-lg font-medium mt-4 text-emerald-50">{f.title}</h4>
              <p className="text-sm text-stone-400 mt-1.5 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <GetTheApp />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <DisclaimerBanner />
      </section>
    </div>
  );
}
